import { createContext, MutableRefObject } from 'react';
import { type IVariationThumbnailRenderer } from '@bynder-studio/render-core';

export type TempVariationProps = {
    oldId?: string;
    newValues?: {
        [pageId: string]: {
            [elementId: string]: Set<unknown>;
        };
    };
    hasUnsavedChangesTimestamp?: number;
    hasUnsavedChanges?: boolean;
};

export type Page = {
    aspectRatio: string;
    creativeVersionPageId: number;
    containsTransparency: boolean;
    creativeThumbnailUrl: string;
    displayOrder: number;
    elements: {
        [key: string]: {
            [key: string]: unknown;
            duration?: number;
            activeDropdownElementId?: number | null;
        };
    };
    failureReason: null;
    hasDeletedAsset: false;
    pageHeight: number;
    pageName: string;
    pageWidth: number;
    previewRendition: {
        id: string;
        type: string;
        status: string;
        renderProgress: number;
        storagePath: string;
    };
    properties: {
        elementProperty: {
            id: number;
            name: string;
            templateElementId: number;
            type: string;
        };
        value: Record<string, unknown>;
    }[];
    renderStatus: null;
    sizeId: number;
    updated: string;
    variationId: string;
    variationStatus: string;
    hasOversetText?: boolean;
};

type CommonVariationProps = {
    id: string;
    name: string;
    aggregateBase: 'SET';
    created: string;
    creator: {
        identityId: string;
        firstName: string;
        lastName: string;
    };
    customerId: number;
    displayOrder: number;
    updated: string;
    variationSetId: string;
};

export type RawVariation = CommonVariationProps & {
    entries: Page[];
};

export type Variation = CommonVariationProps &
    TempVariationProps & {
        sizes: {
            [key: string]: Page;
        };
    };

export type VariationsContextData = {
    variations: Variation[];
    total: number;
    isLoading: boolean;
    isLastPage: boolean;
    isLoadingNextPage: boolean;
    isLimitExceeded: boolean;
    page: number;
    currentVariation: string;
    deletedAssets: Array<string>;
    selectVariation: (id: string) => void;
    selectNextVariation: () => void;
    selectPreviousVariation: () => void;
    getVariations: (pageId: number) => Promise<Variation[]>;
    getVariationsAndSave: () => void;
    addVariation: () => Promise<Variation>;
    renameVariation: (variationId: string, variationName: string) => Promise<void>;
    renameVariationLongError: () => void;
    deleteVariations: (variationIds: string[]) => Promise<void>;
    hasValidPage: () => boolean;
    hasInvalidVariations: () => boolean;
    duplicateVariation: (variationId: string) => Promise<Variation>;
    batchDuplicateVariations: (variationIds: string[]) => Promise<{ creative: Record<string, unknown>; id: string }[]>;
    saveVariation: (variationId: string, callback?: () => void, resetPages?: number[]) => Promise<void>;
    addNewUnsavedValue: ({
        variation,
        pageId,
        elementId,
        newValues,
    }: {
        variation: Variation;
        pageId: number;
        elementId: number;
        newValues: TempVariationProps['newValues'];
    }) => void;
    updateVariationElement: (variationId: string, elementId: string, newValue: Record<string, unknown>) => void;
    updateVariationGlobalProperty: (
        elementId: string | number,
        newValue: Record<string, unknown>,
        pageId?: string | number,
    ) => void;
    maxVariationCount: number;
    isCurrentVariationPending: boolean;
    variationSaveInProgressMap: MutableRefObject<object>;
    removeDeletedAsset: (id: number) => void;
    oversetPagesByVariation: Map<string, Set<number>>;
    isVariationOverset: (variationSetId: string) => boolean;
    isPageOverset: (variationSetId: string, pageId: number) => boolean;
    updateVariationOverset: ({ isValid, currentVariation }: { isValid: boolean; currentVariation: string }) => void;
    sortVariations: (variations: Variation[]) => void;
    saveVariationsOrdering: (variations: Variation[]) => Promise<void>;
    variationUpdatesCount: number;
};

export const VariationsContext = createContext<VariationsContextData>({} as VariationsContextData);

export type VariationsThumbnailsContextValue = {
    variationThumbnailRenderer: IVariationThumbnailRenderer;
    swapVariationThumbnailModel: (variation: Variation) => void;
};

export const VariationsThumbnailsContext = createContext({} as VariationsThumbnailsContextValue);
